import { assertNotEmpty } from '@festina-life/core'
import { fplusHighlighterProps, languageID } from '@festina-life/fplus'
import {
  createFPlusGrammar,
  createFPlusTextMateThemes,
  ThemeType,
} from '@festina-life/fplus-monaco'
import { registerCustomLanguage, registerCustomTheme } from '@pierre/diffs'
import type { ThemeRegistration } from 'shiki'

/**
 * The extension the diff renderer infers the F-plus language from. Each diff
 * side is given a file name carrying it, because the renderer reads the
 * language off the file name rather than being told it.
 */
export const fplusFileExtension = 'fplus'

let isRegistered = false

/**
 * The surfaces the diff panel paints itself in, as hex because the diff
 * renderer derives its row, gutter and separator tones from this colour and
 * cannot resolve custom properties. They are the portal's raised-surface pair
 * (`slate-100` / `slate-800`, the latter being `--color-amaze-ink`), so the
 * panel is set off from the paper it sits on the way other raised surfaces
 * are, rather than blending into it or cutting across it in another hue.
 */
const diffSurfaceColors: Record<ThemeType, string> = {
  [ThemeType.light]: '#f1f5f9',
  [ThemeType.dark]: '#1d293d',
}

/**
 * Puts a theme on the diff panel's own surface. The viewer paints no
 * background and so takes the colour of whatever it is placed on, whereas the
 * diff renderer always paints one and tones its rows, gutters and separators
 * from it. Giving it a portal surface rather than the editor's keeps the panel
 * in the page's palette while still standing out from the paper behind it.
 */
const onDiffSurface = (
  theme: ThemeRegistration,
  background: string,
): ThemeRegistration => ({
  ...theme,
  colors: { ...theme.colors, 'editor.background': background },
})

/**
 * Teaches the diff renderer the F-plus language and the F-plus themes. It is
 * the same generated grammar and the same TextMate themes the editor and the
 * viewer use, so the three surfaces cannot drift apart.
 *
 * Registering twice is a no-op: the renderer logs an error and gives up on a
 * duplicate, so this is guarded rather than left to it.
 */
export const registerFPlusDiffHighlighting = (): void => {
  if (isRegistered) {
    return
  }
  isRegistered = true

  registerCustomLanguage(
    languageID,
    () => Promise.resolve({ default: [createFPlusGrammar(languageID)] }),
    [fplusFileExtension],
  )

  for (const theme of createFPlusTextMateThemes(fplusHighlighterProps)) {
    const { name } = theme
    assertNotEmpty(name)

    const background = diffSurfaceColors[name as ThemeType]
    assertNotEmpty(background)

    registerCustomTheme(name, () =>
      Promise.resolve(onDiffSurface(theme, background)),
    )
  }
}
